import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/cn';

export interface TabItem {
  id: string;
  label: ReactNode;
  panel: ReactNode;
}

interface TabsProps {
  readonly items: TabItem[];
  readonly defaultId?: string;
  readonly ariaLabel?: string;
}

// Headless tabbed container with URL-fragment sync. The active tab is
// driven by `location.hash` (e.g. `/admin/trust#sigstore`) so bookmarks
// and browser-back navigate between tabs without reloading the parent
// page. Keyboard navigation follows the WAI-ARIA Authoring Practices
// pattern: ArrowLeft/Right move focus, Home/End jump to ends, Space or
// Enter activate.
export function Tabs({ items, defaultId, ariaLabel }: TabsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const fallbackId = defaultId ?? items[0]?.id;
  const fragment = location.hash.replace(/^#/, '');
  const matched = items.find((t) => t.id === fragment);
  const activeId = matched ? matched.id : fallbackId;
  const activeIndex = Math.max(
    0,
    items.findIndex((t) => t.id === activeId),
  );

  const [focusIndex, setFocusIndex] = useState(activeIndex);

  useEffect(() => {
    setFocusIndex(activeIndex);
  }, [activeIndex]);

  function selectIndex(idx: number) {
    const item = items[idx];
    if (!item) return;
    navigate(`${location.pathname}${location.search}#${item.id}`, { replace: false });
  }

  function onKey(e: KeyboardEvent<HTMLButtonElement>) {
    let next: number;
    if (e.key === 'ArrowRight') next = (focusIndex + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (focusIndex - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      selectIndex(focusIndex);
      return;
    } else return;
    e.preventDefault();
    setFocusIndex(next);
    tabRefs.current[next]?.focus();
    selectIndex(next);
  }

  return (
    <div>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="flex flex-wrap gap-1 border-b border-border mb-6"
      >
        {items.map((tab, idx) => {
          const isActive = tab.id === activeId;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => selectIndex(idx)}
              onKeyDown={onKey}
              className={cn(
                'px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nebula/50 rounded-t-md',
                isActive
                  ? 'border-nebula text-nebula'
                  : 'border-transparent text-fg-muted hover:text-fg hover:bg-bg-hover/40',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {items.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`tabpanel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
          hidden={tab.id !== activeId}
        >
          {tab.id === activeId ? tab.panel : null}
        </div>
      ))}
    </div>
  );
}
